/**
 * Husn Bazar mobile navigation drawer.
 *
 * The Figma MobileMenu frame (280x862) shows only the resting panel — it
 * specifies no trigger, no scrim and no transitions. The open/close behaviour
 * here is invented, built to be keyboard-operable:
 *
 *   - focus moves into the panel on open and returns to the trigger on close
 *   - Tab and Shift+Tab are trapped inside the panel while it is open
 *   - Escape closes
 *   - clicking the scrim closes
 *   - background scroll is locked while open
 *   - the drawer auto-closes if the viewport grows past the 990px breakpoint,
 *     so a rotate or resize cannot leave an orphaned panel over the desktop bar
 *
 * Uses no Dawn internals, so it cannot drift when Dawn updates.
 */
class HbNavDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-hb-drawer]');
    this.panel = this.querySelector('[data-hb-panel]');
    this.openers = this.querySelectorAll('[data-hb-drawer-open]');
    this.closers = this.querySelectorAll('[data-hb-drawer-close]');

    if (!this.drawer || !this.panel) return;

    this.onKeydown = this.onKeydown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);

    this.openers.forEach((el) => el.addEventListener('click', () => this.open()));
    this.closers.forEach((el) => el.addEventListener('click', () => this.close()));

    this.viewport = window.matchMedia('(min-width: 990px)');
    this.viewport.addEventListener('change', this.onViewportChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.viewport?.removeEventListener('change', this.onViewportChange);
    this.unlockScroll();
  }

  get isOpen() {
    return this.drawer.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;

    this.lastFocused = document.activeElement;
    this.drawer.setAttribute('open', '');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'true'));
    this.lockScroll();
    document.addEventListener('keydown', this.onKeydown);

    // Focus the first focusable thing in the panel — usually the close button.
    this.focusable()[0]?.focus();
  }

  close() {
    if (!this.isOpen) return;

    this.drawer.removeAttribute('open');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    this.unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);

    // Return focus where the user left it, not to the top of the document.
    if (this.lastFocused instanceof HTMLElement && this.lastFocused.isConnected) {
      this.lastFocused.focus();
    }
  }

  onViewportChange(event) {
    if (event.matches) this.close();
  }

  /** Visible, focusable descendants of the panel, in DOM order. */
  focusable() {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(this.panel.querySelectorAll(selector)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = this.focusable();
    if (items.length === 0) {
      // Nothing to focus: keep focus from escaping to the background.
      event.preventDefault();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];

    // Focus may sit outside the panel if the DOM changed under us; pull it back.
    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  lockScroll() {
    this.scrollY = window.scrollY;
    document.body.classList.add('hb-scroll-locked');
  }

  unlockScroll() {
    document.body.classList.remove('hb-scroll-locked');
  }
}

if (!customElements.get('hb-nav-drawer')) {
  customElements.define('hb-nav-drawer', HbNavDrawer);
}

/**
 * Search overlay — Figma SearchOverlay (14:2208).
 *
 * The frame shows only the resting panel, so open/close, the scrim, focus
 * trapping and Escape are invented, matching the nav drawer's behaviour so the
 * two overlays feel the same. Search itself is Shopify's; this only manages
 * visibility and focus.
 */
class HbSearchOverlay extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-hb-search]');
    this.panel = this.querySelector('[data-hb-search-panel]');
    this.field = this.querySelector('.hb-search__field');
    this.openers = document.querySelectorAll('[data-hb-search-open]');
    this.closers = this.querySelectorAll('[data-hb-search-close]');

    if (!this.overlay || !this.panel) return;

    this.onKeydown = this.onKeydown.bind(this);
    this.openers.forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      })
    );
    this.closers.forEach((el) => el.addEventListener('click', () => this.close()));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('hb-scroll-locked');
  }

  get isOpen() {
    return this.overlay.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.lastFocused = document.activeElement;
    this.overlay.setAttribute('open', '');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('hb-scroll-locked');
    document.addEventListener('keydown', this.onKeydown);
    // Put the caret straight in the field — that is the only reason to open this.
    this.field?.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.overlay.removeAttribute('open');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('hb-scroll-locked');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.lastFocused instanceof HTMLElement && this.lastFocused.isConnected) {
      this.lastFocused.focus();
    }
  }

  focusable() {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(this.panel.querySelectorAll(selector)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const items = this.focusable();
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];

    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('hb-search-overlay')) {
  customElements.define('hb-search-overlay', HbSearchOverlay);
}

/**
 * Cart drawer promo code.
 *
 * Shopify has no API for applying a discount code to the cart — not in Liquid and
 * not in the Cart AJAX API. The only supported route is /discount/CODE, which
 * applies the code to the session and then redirects. A GET form cannot build a
 * path segment, so submit is intercepted here.
 *
 * Delegated from the document because Dawn re-renders the cart drawer via the
 * Section Rendering API, which replaces the form node.
 */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-hb-promo]');
  if (!form) return;

  const input = form.querySelector('[data-hb-promo-input]');
  const code = input?.value.trim();
  if (!code) {
    event.preventDefault();
    input?.focus();
    return;
  }

  event.preventDefault();
  const redirect = window.location.pathname + window.location.search;
  window.location.assign(
    `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`
  );
});
